import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TablePaginationConfig } from 'antd/es/table';
import { OwnerAndAssigneeType } from '@bb/domain/dist/types/SupraLockbox';
import { Button, Modal, Table, Tag } from 'components';
import iconLibrary from 'components/Icon/iconLibrary';
import { PopulatedUser } from 'api/UserController/getProfile';
import { CachedOrg } from '@bb/automaton';
import type { SupraLockboxJsonType } from '@/app-package';
import { buildLockboxAssignConfirmMessage } from '../../../utils';

export type OwnerAndAssigneeData = {
  subjectId: string;
  type: 'org' | 'branch' | 'user' | 'office';
  location: string | undefined;
  address?: string;
  subjectOrgMlsId?: string;
};

type LockboxTableProps = {
  caption: string;
  lockboxData: SupraLockboxJsonType[];
  totalLockboxCount: number;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  scrollHeight: number;
  unassignLockbox: (serialNumber: string, userId: string) => Promise<void>;
  deselectLockbox: (serialNumber: string) => void;
  loadingData: boolean;
  user?: (PopulatedUser & { org: CachedOrg }) | null;
  assigneeSelection?: OwnerAndAssigneeData;
  ownerSelection?: OwnerAndAssigneeData;
  showDeselect?: boolean | undefined;
  showUnassign?: boolean | undefined;
};

const LockboxTable = ({
  caption,
  lockboxData,
  page,
  setPage,
  pageSize,
  scrollHeight,
  totalLockboxCount,
  unassignLockbox,
  deselectLockbox,
  loadingData,
  assigneeSelection,
  ownerSelection,
  showDeselect,
  showUnassign,
}: LockboxTableProps) => {
  useEffect(() => {
    if (lockboxData.length === 0) {
      setPage(1);
    }
  }, [lockboxData, setPage]);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [unassignData, setUnassignData] = useState<{
    serialNumber: string;
    listingText: string;
    isAssignee: boolean;
    ownerType: string;
    assigneeType: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleUnassignClick = (
    serialNumber: string,
    listingText: string,
    assigneeId: string,
    ownerType: string,
    assigneeType: string,
    assigneeBranchIndex: string,
  ) => {
    setUnassignData({
      serialNumber,
      listingText,
      isAssignee:
        Boolean(assigneeSelection) &&
        assigneeId === assigneeSelection.subjectId &&
        (assigneeType !== 'branch' ||
          assigneeBranchIndex === assigneeSelection.location),
      ownerType,
      assigneeType,
    });
    setConfirmVisible(true);
  };

  // Return the userId to unassign based on the selection type
  const getUnassignUserId = (selection?: OwnerAndAssigneeData) => {
    if (!selection) return '';
    if (selection.type === 'branch') {
      return `${selection.subjectId}-${selection.location}`;
    }
    if (selection.type === 'office') {
      return selection.subjectOrgMlsId ?? '';
    }
    return selection.subjectId;
  };

  const handleConfirmOk = async () => {
    setIsLoading(true);
    if (unassignData && assigneeSelection) {
      await unassignLockbox(
        unassignData.serialNumber,
        getUnassignUserId(assigneeSelection),
      );
    }
    if (
      unassignData &&
      ownerSelection &&
      // Skip duplicate unassignment if Owner and Assignee are the same subject
      ownerSelection.subjectId !== assigneeSelection?.subjectId
    ) {
      await unassignLockbox(
        unassignData.serialNumber,
        getUnassignUserId(ownerSelection),
      );
    }
    setIsLoading(false);
    setConfirmVisible(false);
  };

  const handleConfirmCancel = () => {
    setConfirmVisible(false);
  };

  const titleColumn = {
    title: 'Serial Number',
    dataIndex: 'serialNumber',
    key: 'serialNumber',
    render: (serial: string, lockbox: SupraLockboxJsonType) => {
      if (lockbox.id === 'notFound') return serial;
      return `${lockbox.productCode === 'iBox' ? 'iBox BT LE' : 'Invoy'} ${serial}`;
    },
  };

  const statusTagColumn = {
    title: 'status',
    dataIndex: 'status',
    key: 'status',
    render: (_: any, lockbox: SupraLockboxJsonType) => {
      if (lockbox.id === 'notFound')
        return <StyleTag color="red">Not found</StyleTag>;
      if (lockbox.lockboxStatus === 'Assigned')
        return <StyleTag color="volcano">Already assigned</StyleTag>;
    },
  };

  const unassignColumn = {
    title: '',
    dataIndex: 'unassign',
    key: 'unassign',
    align: 'right',
    render: (
      _: any,
      {
        serialNumber,
        listing,
        assignee,
        ownerType,
        assigneeType,
        assigneeBranchIndex,
      }: any,
    ) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation();
            handleUnassignClick(
              serialNumber,
              listing?.line1
                ? `${listing.line1} ${listing?.line2 ?? ''}`.trim()
                : '',
              assignee ?? '',
              ownerType,
              assigneeType,
              assigneeBranchIndex,
            );
          }}
        >
          <iconLibrary.antdCloseCircleOutlined antdprops={{}} />
        </Button>
      ),
  };
  const deselectColumn = {
    title: '',
    dataIndex: 'deselect',
    key: 'deselect',
    align: 'right',
    render: (_: any, { serialNumber }: any) => (
        <Button
          type="link"
          onClick={e => {
            e.stopPropagation();
            deselectLockbox(serialNumber);
          }}
        >
          <iconLibrary.antdCloseCircleOutlined antdprops={{}} />
        </Button>
      ),
  };

  const onChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setPage(pagination.current);
    }
  };

  const selectColumns = () => {
    const columns: any[] = [titleColumn];
    if (showUnassign) columns.push(unassignColumn);
    if (showDeselect) columns.push(...[statusTagColumn, deselectColumn]);
    return columns;
  };

  return (
    <>
      <StyledDiv>
        <HeaderSection>
          <StyledCaption>{caption}</StyledCaption>
          <StyledLockboxCount>{totalLockboxCount}</StyledLockboxCount>
        </HeaderSection>
        <StyledTable
          showHeader={false}
          loading={loadingData}
          pagination={{ pageSize, total: totalLockboxCount, current: page }}
          dataSource={lockboxData}
          columns={selectColumns()}
          onChange={onChange}
          scroll={{ y: scrollHeight }}
          onRow={lockbox => ({
            style: { cursor: 'pointer' },
            'data-cy': `lockbox-row-${lockbox._id}`,
          })}
          tableLayout="auto"
        />
      </StyledDiv>
      <Modal
        title="Confirm Unassign"
        open={confirmVisible}
        onCancel={handleConfirmCancel}
        footer={[
          <Button key="cancel" onClick={handleConfirmCancel}>
            Cancel
          </Button>,
          <Button
            key="unassign"
            type="primary"
            onClick={handleConfirmOk}
            loading={isLoading}
          >
            Unassign
          </Button>,
        ]}
      >
        <p>
          {buildLockboxAssignConfirmMessage({
            action: 'Unassigning',
            ownerType: unassignData?.ownerType as OwnerAndAssigneeType,
            assigneeType: unassignData?.assigneeType as OwnerAndAssigneeType,
            isAssignee: !!unassignData?.isAssignee,
            listingAddress: unassignData?.listingText,
          })}
        </p>
      </Modal>
    </>
  );
};

export default LockboxTable;

const StyledDiv = styled.div`
  width: 100%;
`;

const StyledCaption = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const StyledTable = styled(Table)`
  .ant-pagination {
    justify-content: center;
  }
  width: 100%;
`;

const StyledLockboxCount = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const StyleTag = styled(Tag)`
  border-radius: 2px;
  margin-left: 5px;
`;
